import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, CreditCard, Upload, CheckCircle, AlertCircle, Phone, FileImage, Video, ExternalLink, Bell, RotateCcw, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { useAuth } from '../context/AuthContext';
import { getAvailableSlots, getPaymentMethods, createBooking, uploadBookingProofFile, getMyBookings, requestReschedule, uploadRescheduleDocument, uploadUlasDeclaration, getMyNotifications } from '../utils/api';
import { toast } from 'sonner';

const Booking = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Form State
  const [step, setStep] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isUlas, setIsUlas] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [duration, setDuration] = useState(1);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [viberContact, setViberContact] = useState('');
  const [notes, setNotes] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [ulasDeclarationFile, setUlasDeclarationFile] = useState(null);
  const [uploadingUlasDoc, setUploadingUlasDoc] = useState(false);
  const [legalCategory, setLegalCategory] = useState('');
  
  // User's bookings
  const [myBookings, setMyBookings] = useState([]);
  const [showViberModal, setShowViberModal] = useState(false);
  const [selectedViberBooking, setSelectedViberBooking] = useState(null);
  const [showMyBookings, setShowMyBookings] = useState(false);
  
  // Reschedule state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleSlot, setRescheduleSlot] = useState('');
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleFile, setRescheduleFile] = useState(null);
  const [uploadingRescheduleDoc, setUploadingRescheduleDoc] = useState(false);
  
  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      loadPaymentMethods();
      loadMyBookings();
      loadNotifications();
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (selectedDate) {
      loadAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const loadPaymentMethods = async () => {
    try {
      const res = await getPaymentMethods();
      setPaymentMethods(res.data.methods);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    }
  };

  const loadAvailableSlots = async (date) => {
    try {
      setLoading(true);
      const res = await getAvailableSlots(date);
      setAvailableSlots(res.data.available_slots);
      setPricingInfo(res.data.pricing);
      if (res.data.available_slots.length === 0 && !res.data.is_default_day && !res.data.has_custom_slots) {
        toast.info('Consultations are only available on Tuesdays and Thursdays (9AM-12PM). Admin may open additional slots.');
      }
    } catch (error) {
      toast.error('Failed to load available slots');
    } finally {
      setLoading(false);
    }
  };

  const loadMyBookings = async () => {
    try {
      const res = await getMyBookings();
      setMyBookings(res.data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const res = await getMyNotifications();
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const loadRescheduleSlots = async (date) => {
    try {
      const res = await getAvailableSlots(date);
      setRescheduleSlots(res.data.available_slots);
    } catch (error) {
      setRescheduleSlots([]);
    }
  };

  const handleOpenReschedule = (booking) => {
    const bookingDate = new Date(booking.date + 'T00:00:00Z');
    const now = new Date();
    const diffDays = Math.floor((bookingDate - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 3) {
      toast.error('Reschedule requests must be made at least 3 days before the scheduled date.');
      return;
    }
    setRescheduleBooking(booking);
    setRescheduleReason('');
    setRescheduleDate('');
    setRescheduleSlot('');
    setRescheduleSlots([]);
    setRescheduleFile(null);
    setShowRescheduleModal(true);
  };

  const handleSubmitReschedule = async () => {
    if (!rescheduleReason.trim() || !rescheduleDate || !rescheduleSlot) {
      toast.error('Please fill in all fields');
      return;
    }
    setRescheduleLoading(true);
    try {
      const res = await requestReschedule(rescheduleBooking.id, {
        reason: rescheduleReason.trim(),
        preferred_date: rescheduleDate,
        preferred_time_slot: rescheduleSlot
      });
      // Upload supporting document if provided
      if (rescheduleFile) {
        setUploadingRescheduleDoc(true);
        const formData = new FormData();
        formData.append('file', rescheduleFile);
        await uploadRescheduleDocument(rescheduleBooking.id, formData);
        setUploadingRescheduleDoc(false);
      }
      toast.success('Reschedule request submitted! Admin will review it.');
      setShowRescheduleModal(false);
      setRescheduleBooking(null);
      setRescheduleFile(null);
      loadMyBookings();
      loadNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit reschedule request');
    } finally {
      setRescheduleLoading(false);
      setUploadingRescheduleDoc(false);
    }
  };

  // Calendar helpers
  const bookedDates = useMemo(() => {
    const dates = {};
    myBookings.forEach(b => {
      if (['approved', 'pending', 'payment_uploaded', 'reschedule-requested', 'ulas-pending'].includes(b.status)) {
        const d = new Date(b.date + 'T00:00:00');
        const key = d.toDateString();
        if (!dates[key]) dates[key] = [];
        dates[key].push(b);
      }
    });
    return dates;
  }, [myBookings]);

  const bookingsForSelectedDate = useMemo(() => {
    if (!selectedCalendarDate) return [];
    const key = selectedCalendarDate.toDateString();
    return bookedDates[key] || [];
  }, [selectedCalendarDate, bookedDates]);

  const calendarModifiers = useMemo(() => {
    const approved = [];
    const pending = [];
    const reschedule = [];
    myBookings.forEach(b => {
      const d = new Date(b.date + 'T00:00:00');
      if (b.status === 'approved') approved.push(d);
      else if (b.status === 'pending' || b.status === 'payment_uploaded' || b.status === 'ulas-pending') pending.push(d);
      else if (b.status === 'reschedule-requested') reschedule.push(d);
    });
    return { approved, pending, reschedule };
  }, [myBookings]);

  const calendarModifiersStyles = {
    approved: { backgroundColor: '#dcfce7', color: '#166534', borderRadius: '50%' },
    pending: { backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '50%' },
    reschedule: { backgroundColor: '#f3e8ff', color: '#7c3aed', borderRadius: '50%' }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedSlot('');
  };

  const calculateTotal = () => {
    if (isUlas) return 0;
    return duration * (pricingInfo?.hourly_rate || 1000);
  };

  const handleCreateBooking = async () => {
    if (!selectedDate || !selectedSlot || !viberContact || !notes) {
      toast.error('Please fill in all required fields including your concern');
      return;
    }
    if (!isUlas && !selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }
    if (!legalCategory) {
      toast.error('Please select the nature of your legal concern');
      return;
    }
    if (notes.trim().length < 20) {
      toast.error('Please provide a more detailed description of your legal concern (at least 20 characters)');
      return;
    }

    setLoading(true);
    try {
      const res = await createBooking({
        date: selectedDate,
        time_slot: selectedSlot,
        duration,
        payment_method: isUlas ? 'ulas' : selectedPaymentMethod,
        viber_contact: viberContact,
        notes,
        legal_category: legalCategory,
        is_ulas: isUlas,
        terms_accepted: true
      });
      setCreatedBookingId(res.data.booking_id);
      if (isUlas) {
        toast.success('ULAS booking created! Please upload your Qualified Beneficiary Declaration.');
      } else {
        toast.success('Booking created! Please upload your payment proof.');
      }
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadProof = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file (JPG, PNG, WEBP)');
      return;
    }
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await uploadBookingProofFile(createdBookingId, formData);
      toast.success('Payment proof uploaded! Your booking is now pending admin approval.');
      setStep(5);
      loadMyBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload payment proof');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleUploadUlasDeclaration = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, image, or document file');
      return;
    }
    setUploadingUlasDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await uploadUlasDeclaration(createdBookingId, formData);
      toast.success('ULAS declaration uploaded! Your booking is pending admin verification.');
      setStep(5);
      loadMyBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload declaration');
    } finally {
      setUploadingUlasDoc(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      payment_uploaded: 'bg-orange-100 text-orange-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800',
      'reschedule-requested': 'bg-purple-100 text-purple-800',
      'ulas-pending': 'bg-yellow-100 text-yellow-800',
      'ulas-rejected': 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'Pending',
      payment_uploaded: 'Payment Uploaded',
      approved: 'Approved',
      rejected: 'Rejected',
      completed: 'Completed',
      cancelled: 'Cancelled',
      'reschedule-requested': 'Reschedule Requested',
      'ulas-pending': 'ULAS Pending Verification',
      'ulas-rejected': 'ULAS Not Qualified'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // At least 1 day in advance
    return today.toISOString().split('T')[0];
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] py-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] py-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
            <CalendarIcon className="h-16 w-16 text-[#8B1818] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Book a Consultation</h1>
            <p className="text-slate-600 mb-6">
              Please login to book a legal consultation with our experts.
            </p>
            <Button onClick={() => navigate('/login')} className="btn-primary">
              Login to Book
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Book a Consultation
          </h1>
          <p className="text-slate-600">
            Schedule a legal consultation with our expert team via Viber
          </p>
        </div>

        {/* Toggle My Bookings */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            {showMyBookings && notifications.length > 0 && (
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors"
                data-testid="notifications-toggle-btn"
              >
                <Bell className="h-5 w-5 text-amber-600" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {notifications.length}
                </span>
              </button>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => { setShowMyBookings(!showMyBookings); setShowNotifications(false); setSelectedCalendarDate(null); }}
            className="text-[#8B1818] border-[#8B1818]"
            data-testid="toggle-my-bookings-btn"
          >
            {showMyBookings ? 'New Booking' : `My Bookings (${myBookings.length})`}
          </Button>
        </div>

        {/* Notifications Panel */}
        {showMyBookings && showNotifications && notifications.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-5" data-testid="notifications-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-amber-900 flex items-center gap-2">
                <Bell className="h-4 w-4" /> Upcoming Reminders
              </h3>
              <button onClick={() => setShowNotifications(false)} className="text-amber-600 hover:text-amber-800">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {notifications.map((n, i) => (
                <div key={i} className={`p-3 rounded-lg text-sm flex items-start gap-3 ${
                  n.urgency === 'today' ? 'bg-red-100 text-red-800 border border-red-200' :
                  n.urgency === 'tomorrow' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
                  n.urgency === 'soon' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                  n.urgency === 'info' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                  'bg-white text-slate-700 border border-slate-200'
                }`} data-testid={`notification-item-${i}`}>
                  <CalendarIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{n.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Bookings with Calendar */}
        {showMyBookings ? (
          <div className="space-y-6">
            {/* Calendar Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6" data-testid="my-bookings-calendar">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-[#8B1818]" /> My Booking Calendar
              </h2>
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedCalendarDate}
                    onSelect={(date) => setSelectedCalendarDate(date)}
                    month={calendarDate}
                    onMonthChange={setCalendarDate}
                    modifiers={calendarModifiers}
                    modifiersStyles={calendarModifiersStyles}
                    className="rounded-md border"
                  />
                </div>
                <div className="flex-1">
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 mb-4 text-xs">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-200 inline-block"></span> Approved</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-yellow-200 inline-block"></span> Pending</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-purple-200 inline-block"></span> Reschedule Requested</span>
                  </div>
                  {/* Selected date bookings */}
                  {selectedCalendarDate ? (
                    bookingsForSelectedDate.length > 0 ? (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-slate-700">
                          Bookings on {selectedCalendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        </h3>
                        {bookingsForSelectedDate.map(b => (
                          <div key={b.id} className="p-3 border rounded-lg bg-slate-50">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-semibold text-sm">{b.time_slot} — {b.duration}hr</p>
                                <p className="text-xs text-slate-500">₱{b.total_amount?.toLocaleString()}</p>
                              </div>
                              {getStatusBadge(b.status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No bookings on this date.</p>
                    )
                  ) : (
                    <p className="text-sm text-slate-500">Select a date on the calendar to view bookings.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Bookings List */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6" data-testid="my-bookings-list">
              <h2 className="text-xl font-bold text-slate-900 mb-4">All My Bookings</h2>
              {myBookings.length === 0 ? (
                <p className="text-slate-600 text-center py-8">No bookings yet.</p>
              ) : (
                <div className="space-y-4">
                  {myBookings.map((booking) => (
                    <div key={booking.id} className="border rounded-lg p-4" data-testid={`booking-card-${booking.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {booking.date} at {booking.time_slot}
                          </p>
                          <p className="text-sm text-slate-600">
                            Duration: {booking.duration} hour(s) | ₱{booking.total_amount?.toLocaleString()}
                          </p>
                          <p className="text-sm text-slate-600">
                            Payment: {booking.payment_method?.toUpperCase()}
                          </p>
                          {booking.invoice_number && (
                            <p className="text-sm text-green-600 font-medium mt-1">
                              Invoice: {booking.invoice_number}
                            </p>
                          )}
                        </div>
                        {getStatusBadge(booking.status)}
                      </div>
                      {/* Viber Meeting Button */}
                      {booking.status === 'approved' && booking.viber_link && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <button
                            onClick={() => { setSelectedViberBooking(booking); setShowViberModal(true); }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                            data-testid={`join-viber-${booking.id}`}
                          >
                            <Video className="h-4 w-4" />
                            Join Viber Meeting
                            <ExternalLink className="h-3.5 w-3.5 ml-1" />
                          </button>
                        </div>
                      )}
                      {/* Reschedule Request Button for approved bookings */}
                      {booking.status === 'approved' && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <button
                            onClick={() => handleOpenReschedule(booking)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[#8B1818] border border-[#8B1818] rounded-lg hover:bg-red-50 transition-colors font-medium text-sm"
                            data-testid={`request-reschedule-${booking.id}`}
                          >
                            <RotateCcw className="h-4 w-4" />
                            Request Reschedule
                          </button>
                        </div>
                      )}
                      {/* Reschedule requested info */}
                      {booking.status === 'reschedule-requested' && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="text-sm bg-purple-50 p-3 rounded-lg border border-purple-200">
                            <p className="text-purple-700 font-medium mb-1">Reschedule Requested</p>
                            {booking.reschedule_reason && (
                              <p className="text-purple-600 text-xs">Reason: {booking.reschedule_reason}</p>
                            )}
                            {booking.requested_new_date && (
                              <p className="text-purple-600 text-xs mt-1">
                                Preferred: {booking.requested_new_date} at {booking.requested_new_time_slot}
                              </p>
                            )}
                            <p className="text-purple-500 text-xs mt-1">Waiting for admin review...</p>
                          </div>
                        </div>
                      )}
                      {/* Reschedule rejection notification */}
                      {booking.reschedule_rejection_note && booking.status === 'approved' && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="text-sm bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <p className="text-blue-800 font-medium mb-1">Reschedule Request Declined</p>
                            <p className="text-blue-700 text-xs">{booking.reschedule_rejection_note}</p>
                            <p className="text-blue-600 text-xs mt-1">Your original booking date has been retained.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-8">
              {[1, 2, 3, 4, 5].map((s) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-bold text-sm ${
                    step >= s ? 'bg-[#8B1818] text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {step > s ? <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" /> : s}
                  </div>
                  {s < 5 && (
                    <div className={`w-8 sm:w-12 h-1 mx-1 ${step > s ? 'bg-[#8B1818]' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Step Labels */}
            <div className="flex justify-between text-xs sm:text-sm text-slate-600 mb-8 px-2">
              <span className={step === 1 ? 'text-[#8B1818] font-medium' : ''}>Terms</span>
              <span className={step === 2 ? 'text-[#8B1818] font-medium' : ''}>Schedule</span>
              <span className={step === 3 ? 'text-[#8B1818] font-medium' : ''}>Details</span>
              <span className={step === 4 ? 'text-[#8B1818] font-medium' : ''}>{isUlas ? 'Declaration' : 'Payment'}</span>
              <span className={step === 5 ? 'text-[#8B1818] font-medium' : ''}>Done</span>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
              {/* Step 1: Terms & Conditions */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <AlertCircle className="h-6 w-6 text-[#8B1818]" />
                    <h2 className="text-xl font-bold text-slate-900">Terms & Conditions</h2>
                  </div>
                  <div className="text-sm text-slate-700 bg-slate-50 rounded-xl p-5 max-h-[400px] overflow-y-auto space-y-4 border border-slate-200" data-testid="terms-content">
                    <h3 className="font-bold text-slate-900">LEGAL CONSULTATION TERMS AND CONDITIONS</h3>
                    <p>By booking a consultation through LexDoRight, you agree to the following terms and conditions:</p>

                    <h4 className="font-semibold text-slate-800 mt-3">1. CONFIDENTIALITY & ATTORNEY-CLIENT PRIVILEGE</h4>
                    <p>All communications during the consultation are protected by attorney-client privilege. Both parties shall treat all information exchanged as strictly confidential. This privilege belongs to the client and can only be waived by the client.</p>

                    <h4 className="font-semibold text-slate-800 mt-3">2. PROHIBITION ON RECORDING</h4>
                    <p><strong>Under no circumstances shall any party engage in screen recording, audio recording, video recording, screenshotting, or any other form of capturing the consultation session.</strong> This prohibition applies to all participants, attendees, and any third parties. Violation of this provision may result in immediate termination of the consultation, forfeiture of fees paid, and may give rise to legal liability under applicable privacy laws including but not limited to the Data Privacy Act of 2012 (Republic Act No. 10173) and its implementing rules and regulations.</p>

                    <h4 className="font-semibold text-slate-800 mt-3">3. DATA PRIVACY</h4>
                    <p>In compliance with the Data Privacy Act of 2012, your personal data shall be collected, processed, and stored solely for the purpose of this legal consultation. We implement appropriate organizational, physical, and technical security measures to protect your personal information. Your data will not be shared with third parties without your explicit consent, except as required by law or court order.</p>

                    <h4 className="font-semibold text-slate-800 mt-3">4. SCOPE OF CONSULTATION</h4>
                    <p>The consultation constitutes legal advice based on the facts and documents presented during the session. It does not create a continuing attorney-client relationship beyond the scope of the booked session unless a separate engagement letter is executed.</p>

                    <h4 className="font-semibold text-slate-800 mt-3">5. CANCELLATION & RESCHEDULING</h4>
                    <p>Rescheduling requests must be made at least three (3) days before the scheduled consultation date. Failure to appear without prior notice may result in forfeiture of fees paid.</p>

                    <h4 className="font-semibold text-slate-800 mt-3">6. ULAS FREE LEGAL AID</h4>
                    <p>Consultations under the Unified Legal Aid Service (ULAS) are available free of charge to qualified beneficiaries as determined under the ULAS rules. Applicants must submit a notarized Qualified Beneficiary Declaration, which is subject to verification by the administering attorney. If upon interview, the applicant is found not to be a qualified beneficiary, the consultation may be terminated, and the applicant may opt to continue under regular paid consultation rates.</p>

                    <h4 className="font-semibold text-slate-800 mt-3">7. LIMITATION OF LIABILITY</h4>
                    <p>The consulting attorney shall not be liable for any outcome arising from the client's own actions or inactions following the consultation. The advice provided is based solely on the facts disclosed during the session.</p>

                    <h4 className="font-semibold text-slate-800 mt-3">8. GOVERNING LAW</h4>
                    <p>These terms shall be governed by and construed in accordance with the laws of the Republic of the Philippines.</p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer group p-4 rounded-lg border border-slate-200 hover:border-[#8B1818] transition-colors" data-testid="terms-accept-label">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-0.5 w-5 h-5 text-[#8B1818] rounded border-slate-300 focus:ring-[#8B1818]"
                      data-testid="terms-accept-checkbox"
                    />
                    <span className="text-sm text-slate-700">
                      I have read, understood, and agree to the <strong>Terms and Conditions</strong>, including the privacy requirements and the prohibition on recording.
                    </span>
                  </label>

                  <Button
                    onClick={() => setStep(2)}
                    disabled={!termsAccepted}
                    className="w-full bg-[#8B1818] text-white hover:bg-[#701313]"
                    data-testid="accept-terms-btn"
                  >
                    I Accept — Proceed to Booking
                  </Button>
                </div>
              )}

              {/* Step 2: Select Date & Time */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <CalendarIcon className="h-6 w-6 text-[#8B1818]" />
                    <h2 className="text-xl font-bold text-slate-900">Select Date & Time</h2>
                  </div>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded mb-6">
                    <p className="text-sm text-amber-900">
                      <strong>Note:</strong> Regular consultation hours are <strong>Tuesdays and Thursdays, 9AM-12PM</strong>. 
                      Admin may open additional time slots as needed.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="date">Select Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      min={getMinDate()}
                      className="mt-2"
                    />
                  </div>

                  {selectedDate && (
                    <div>
                      <Label>Available Time Slots</Label>
                      {loading ? (
                        <div className="text-center py-4">Loading...</div>
                      ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-3 gap-3 mt-2">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setSelectedSlot(slot)}
                              className={`p-3 rounded-lg border text-center transition ${
                                selectedSlot === slot
                                  ? 'bg-[#8B1818] text-white border-[#8B1818]'
                                  : 'bg-white text-slate-700 border-slate-300 hover:border-[#8B1818]'
                              }`}
                            >
                              <Clock className="h-4 w-4 mx-auto mb-1" />
                              {slot}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-slate-600">
                          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                          No available slots for this date.
                        </div>
                      )}
                    </div>
                  )}

                  {selectedSlot && pricingInfo && (
                    <div>
                      <Label>Consultation Type</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2 mb-4">
                        <button
                          onClick={() => { setIsUlas(false); setUlasDeclarationFile(null); }}
                          className={`p-4 rounded-lg border text-center transition ${
                            !isUlas ? 'bg-[#8B1818] text-white border-[#8B1818]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#8B1818]'
                          }`}
                          data-testid="regular-consultation-btn"
                        >
                          <p className="font-bold">Regular</p>
                          <p className="text-sm">Paid Consultation</p>
                        </button>
                        <button
                          onClick={() => { setIsUlas(true); setSelectedPaymentMethod(''); }}
                          className={`p-4 rounded-lg border text-center transition ${
                            isUlas ? 'bg-indigo-700 text-white border-indigo-700' : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-500'
                          }`}
                          data-testid="ulas-consultation-btn"
                        >
                          <p className="font-bold">ULAS</p>
                          <p className="text-sm">Free Legal Aid</p>
                        </button>
                      </div>
                      {isUlas && (
                        <div className="bg-indigo-50 border-l-4 border-indigo-500 p-3 rounded mb-4 text-sm text-indigo-800">
                          <strong>ULAS Free Legal Aid:</strong> Pursuant to the Unified Legal Aid Service (ULAS) rules, this consultation is free of charge (₱0) subject to verification that you are a qualified beneficiary.
                          You will be required to submit a <strong>notarized Qualified Beneficiary Declaration</strong>.
                          <a
                            href="/api/files/ULAS_Qualified_Beneficiary_Declaration.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1.5 text-indigo-700 font-semibold hover:underline"
                            data-testid="download-ulas-form-schedule"
                          >
                            <FileImage className="h-4 w-4" />
                            Download Qualified Beneficiary Declaration Form (PDF)
                          </a>
                        </div>
                      )}
                      <Label>Duration</Label>
                      <div className="grid grid-cols-3 gap-3 mt-2">
                        {pricingInfo.durations.map((d) => (
                          <button
                            key={d.hours}
                            onClick={() => setDuration(d.hours)}
                            className={`p-4 rounded-lg border text-center transition ${
                              duration === d.hours
                                ? 'bg-[#8B1818] text-white border-[#8B1818]'
                                : 'bg-white text-slate-700 border-slate-300 hover:border-[#8B1818]'
                            }`}
                          >
                            <p className="font-bold">{d.hours} Hour{d.hours > 1 ? 's' : ''}</p>
                            <p className="text-sm">{isUlas ? '₱0 (ULAS)' : `₱${d.total.toLocaleString()}`}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button
                      onClick={() => setStep(3)}
                      disabled={!selectedDate || !selectedSlot}
                      className="btn-primary"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Your Details & Payment Method */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <CreditCard className="h-6 w-6 text-[#8B1818]" />
                    <h2 className="text-xl font-bold text-slate-900">Your Details {isUlas ? '' : '& Payment'}</h2>
                  </div>

                  {/* Booking Summary */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-slate-900 mb-2">Booking Summary</h3>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p><strong>Date:</strong> {selectedDate}</p>
                      <p><strong>Time:</strong> {selectedSlot}</p>
                      <p><strong>Duration:</strong> {duration} hour(s)</p>
                      {isUlas && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">ULAS Free Legal Aid</span>
                      )}
                      <p className="text-lg font-bold text-[#8B1818] mt-2">
                        Total: {isUlas ? '₱0 (ULAS)' : `₱${calculateTotal().toLocaleString()}`}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="viber">Viber Contact Number *</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="viber"
                        type="tel"
                        value={viberContact}
                        onChange={(e) => setViberContact(e.target.value)}
                        placeholder="e.g., 09171234567"
                        className="pl-10"
                        required
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      We will contact you via Viber for the consultation
                    </p>
                  </div>

                  {!isUlas && (
                    <div>
                      <Label>Payment Method *</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        {paymentMethods.map((method) => (
                          <button
                            key={method.id}
                            onClick={() => setSelectedPaymentMethod(method.id)}
                            className={`p-4 rounded-lg border text-left transition ${
                              selectedPaymentMethod === method.id
                                ? 'bg-[#8B1818] text-white border-[#8B1818]'
                                : 'bg-white text-slate-700 border-slate-300 hover:border-[#8B1818]'
                            }`}
                          >
                            <p className="font-bold">{method.name}</p>
                          </button>
                        ))}
                      </div>
                      {selectedPaymentMethod && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-blue-800">
                            <strong>Payment Instructions:</strong><br />
                            {paymentMethods.find(m => m.id === selectedPaymentMethod)?.instructions}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label>Nature of Legal Concern *</Label>
                    <p className="text-xs text-slate-500 mt-1 mb-2">Please select the category that best describes your consultation need.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {[
                        { id: 'Government Procurement', label: 'Government Procurement', desc: 'Bidding, contracts, COA issues' },
                        { id: 'Notarization', label: 'Notarization', desc: 'Document authentication & notarial services' },
                        { id: 'Corporate Questions', label: 'Corporate Questions', desc: 'Business formation, compliance, SEC matters' },
                        { id: 'Public Officer Accountability', label: 'Public Officer Accountability', desc: 'Government employee cases, Ombudsman, CSC' },
                        { id: 'Other Legal-related Matters', label: 'Other Legal-related Matters', desc: 'Civil, criminal, labor, family, or other concerns' },
                        { id: 'App Development', label: 'App Development', desc: 'Software, web, and mobile application projects' }
                      ].map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => setLegalCategory(cat.id)}
                          className={`p-3 rounded-lg border text-left transition ${
                            legalCategory === cat.id
                              ? 'bg-[#8B1818] text-white border-[#8B1818]'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-[#8B1818]'
                          }`}
                          data-testid={`legal-category-${cat.id.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <p className="font-semibold text-sm">{cat.label}</p>
                          <p className={`text-xs mt-0.5 ${legalCategory === cat.id ? 'text-white/80' : 'text-slate-500'}`}>{cat.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Brief Summary of Concern *</Label>
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Please describe your legal concern or questions you'd like to discuss during the consultation..."
                      className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent"
                      rows={3}
                      required
                    />
                  </div>

                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setStep(2)}>
                      Back
                    </Button>
                    <Button
                      onClick={handleCreateBooking}
                      disabled={loading || !viberContact || (!isUlas && !selectedPaymentMethod) || !notes || notes.trim().length < 20 || !legalCategory}
                      className="btn-primary"
                    >
                      {loading ? 'Creating...' : (isUlas ? 'Proceed to Declaration Upload' : 'Proceed to Payment')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Upload Payment Proof / ULAS Declaration */}
              {step === 4 && (
                <div className="space-y-6">
                  {isUlas ? (
                    <>
                      <div className="flex items-center space-x-3 mb-6">
                        <Upload className="h-6 w-6 text-indigo-600" />
                        <h2 className="text-xl font-bold text-slate-900">Upload ULAS Declaration</h2>
                      </div>

                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <p className="text-indigo-800">
                          <strong>ULAS Booking Created!</strong> Please upload your notarized Qualified Beneficiary Declaration for verification.
                        </p>
                      </div>

                      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded text-sm text-amber-800">
                        <strong>Important:</strong> The Qualified Beneficiary Declaration must be a notarized document.
                        Your eligibility as a ULAS beneficiary will be verified by the administering attorney during the consultation.
                        If found not qualified, the consultation may be terminated, and you may opt to rebook as a regular paid consultation.
                        <a
                          href="/api/files/ULAS_Qualified_Beneficiary_Declaration.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 text-amber-900 font-semibold hover:underline"
                          data-testid="download-ulas-form-upload"
                        >
                          <FileImage className="h-4 w-4" />
                          Download the form here if you haven't yet
                        </a>
                      </div>

                      <div>
                        <Label className="text-lg font-semibold mb-3 block">Notarized Qualified Beneficiary Declaration *</Label>
                        <label className="cursor-pointer block">
                          <div className={`border-2 border-dashed border-indigo-300 rounded-xl p-8 text-center hover:border-indigo-500 transition-colors ${uploadingUlasDoc ? 'opacity-50' : ''}`}>
                            <FileImage className="h-10 w-10 mx-auto text-indigo-400 mb-3" />
                            <span className="text-slate-600 block mb-2">
                              {uploadingUlasDoc ? 'Uploading...' : 'Click to upload your notarized declaration'}
                            </span>
                            <span className="text-xs text-slate-400">PDF, Image, or Document files accepted</span>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,image/*"
                              onChange={handleUploadUlasDeclaration}
                              className="hidden"
                              disabled={uploadingUlasDoc}
                              data-testid="ulas-declaration-upload"
                            />
                          </div>
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center space-x-3 mb-6">
                        <Upload className="h-6 w-6 text-[#8B1818]" />
                        <h2 className="text-xl font-bold text-slate-900">Upload Payment Proof</h2>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-800">
                          <strong>Booking Created!</strong> Please complete your payment and upload proof.
                        </p>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">Amount to Pay</h3>
                        <p className="text-2xl font-bold text-[#8B1818]">₱{calculateTotal().toLocaleString()}</p>
                        <p className="text-sm text-slate-600 mt-1">
                          Via {paymentMethods.find(m => m.id === selectedPaymentMethod)?.name}
                        </p>
                      </div>

                      <div>
                        <Label className="text-lg font-semibold mb-3 block">Upload Proof of Payment</Label>
                        <label className="cursor-pointer block">
                          <div className={`border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-[#8B1818] transition-colors ${uploadingProof ? 'opacity-50' : ''}`}>
                            <FileImage className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                            <span className="text-slate-600 block mb-2">
                              {uploadingProof ? 'Uploading...' : 'Click to upload your payment screenshot/receipt'}
                            </span>
                            <span className="text-xs text-slate-400">PDF or Image files (JPG, PNG, WEBP)</span>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.webp,image/*"
                              onChange={handleUploadProof}
                              className="hidden"
                              disabled={uploadingProof}
                            />
                          </div>
                        </label>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>Data Retention Notice:</strong> Proofs of payment will be stored for 10 days from the consultation completion date for verification purposes. After this period, the admin will delete them to protect your privacy.
                        </p>
                      </div>

                      <p className="text-sm text-slate-500 text-center">
                        Make sure your proof of payment clearly shows the transaction details including the amount and reference number.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Step 5: Confirmation */}
              {step === 5 && (
                <div className="text-center py-8">
                  <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    {isUlas ? 'ULAS Booking Submitted!' : 'Booking Submitted!'}
                  </h2>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    {isUlas
                      ? 'Your ULAS free consultation booking has been submitted. Your Qualified Beneficiary Declaration is pending admin verification.'
                      : 'Your consultation booking has been submitted and is pending admin approval. You will receive an email confirmation with your invoice once approved.'
                    }
                  </p>
                  
                  <div className="bg-slate-50 rounded-lg p-6 max-w-md mx-auto mb-6">
                    <h3 className="font-semibold text-slate-900 mb-3">What's Next?</h3>
                    <ul className="text-sm text-slate-600 text-left space-y-2">
                      {isUlas ? (
                        <>
                          <li>1. Admin will verify your Qualified Beneficiary Declaration</li>
                          <li>2. Upon verification, your booking will be approved</li>
                          <li>3. During consultation, the attorney will conduct an interview to confirm eligibility</li>
                          <li>4. If not qualified, you may opt to rebook as a paid consultation</li>
                        </>
                      ) : (
                        <>
                          <li>1. Admin will review your payment proof</li>
                          <li>2. Upon approval, you'll receive an invoice via email</li>
                          <li>3. Our team will contact you via Viber before your consultation</li>
                        </>
                      )}
                    </ul>
                  </div>

                  <div className="flex gap-4 justify-center">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowMyBookings(true);
                        setStep(1);
                        setSelectedDate('');
                        setSelectedSlot('');
                        setDuration(1);
                        setSelectedPaymentMethod('');
                        setViberContact('');
                        setNotes('');
                        setProofFile(null);
                        setCreatedBookingId(null);
                        setIsUlas(false);
                        setTermsAccepted(false);
                        setUlasDeclarationFile(null);
                        setLegalCategory('');
                      }}
                    >
                      View My Bookings
                    </Button>
                    <Button
                      className="btn-primary"
                      onClick={() => {
                        setStep(1);
                        setSelectedDate('');
                        setSelectedSlot('');
                        setDuration(1);
                        setSelectedPaymentMethod('');
                        setViberContact('');
                        setNotes('');
                        setProofFile(null);
                        setCreatedBookingId(null);
                        setIsUlas(false);
                        setTermsAccepted(false);
                        setUlasDeclarationFile(null);
                        setLegalCategory('');
                      }}
                    >
                      Book Another
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Pricing Info */}
        {!showMyBookings && step === 2 && (
          <div className="mt-8 bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-4">Consultation Pricing</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">1 Hour</p>
                <p className="text-xl font-bold text-[#8B1818]">₱1,000</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">2 Hours</p>
                <p className="text-xl font-bold text-[#8B1818]">₱2,000</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">3 Hours</p>
                <p className="text-xl font-bold text-[#8B1818]">₱3,000</p>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-sm text-indigo-700 font-medium">ULAS</p>
                <p className="text-xl font-bold text-indigo-600">₱0</p>
                <p className="text-xs text-indigo-500">Free Legal Aid</p>
              </div>
            </div>
          </div>
        )}

        {/* Reschedule Request Modal */}
        {showRescheduleModal && rescheduleBooking && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="reschedule-modal">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-[#8B1818]" />
                  Request Reschedule
                </h3>
                <button onClick={() => { setShowRescheduleModal(false); setRescheduleBooking(null); }}>
                  <X className="h-6 w-6 text-slate-500" />
                </button>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
                <p><strong>Current booking:</strong> {rescheduleBooking.date} at {rescheduleBooking.time_slot}</p>
                <p className="text-slate-600">Duration: {rescheduleBooking.duration} hour(s)</p>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-3 mb-4 text-xs text-amber-800">
                Please note: Your reschedule request will be reviewed by admin. The admin may accept your preferred date or suggest an alternative.
                If the admin rejects the request, your original booking date will be retained.
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="reschedule-reason">Reason for Reschedule *</Label>
                  <textarea
                    id="reschedule-reason"
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    placeholder="Please explain why you need to reschedule..."
                    className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#8B1818] focus:border-transparent"
                    rows={3}
                    data-testid="reschedule-reason-input"
                  />
                </div>

                <div>
                  <Label htmlFor="reschedule-date">Preferred New Date *</Label>
                  <Input
                    id="reschedule-date"
                    type="date"
                    value={rescheduleDate}
                    onChange={(e) => {
                      setRescheduleDate(e.target.value);
                      setRescheduleSlot('');
                      if (e.target.value) loadRescheduleSlots(e.target.value);
                    }}
                    min={getMinDate()}
                    className="mt-2"
                    data-testid="reschedule-date-input"
                  />
                </div>

                {rescheduleDate && (
                  <div>
                    <Label>Preferred Time Slot *</Label>
                    {rescheduleSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {rescheduleSlots.map((slot) => (
                          <button
                            key={slot}
                            onClick={() => setRescheduleSlot(slot)}
                            className={`p-2 rounded-lg border text-center text-sm transition ${
                              rescheduleSlot === slot
                                ? 'bg-[#8B1818] text-white border-[#8B1818]'
                                : 'bg-white text-slate-700 border-slate-300 hover:border-[#8B1818]'
                            }`}
                            data-testid={`reschedule-slot-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 mt-2">No available slots for this date.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Supporting Document Upload */}
              <div className="mt-4">
                <Label>Supporting Document (optional)</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">Upload any document to support your reschedule request (PDF, image, or document files).</p>
                <label className="cursor-pointer block">
                  <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    rescheduleFile ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-[#8B1818]'
                  }`}>
                    <FileImage className="h-6 w-6 mx-auto text-slate-400 mb-1" />
                    {rescheduleFile ? (
                      <span className="text-sm text-green-700 block">{rescheduleFile.name}</span>
                    ) : (
                      <span className="text-sm text-slate-500 block">Click to upload a file</span>
                    )}
                    <span className="text-xs text-slate-400">PDF, JPG, PNG, WEBP, DOC, DOCX</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,image/*"
                      onChange={(e) => setRescheduleFile(e.target.files[0] || null)}
                      className="hidden"
                      data-testid="reschedule-document-input"
                    />
                  </div>
                </label>
                {rescheduleFile && (
                  <button
                    onClick={() => setRescheduleFile(null)}
                    className="text-xs text-red-500 hover:text-red-700 mt-1"
                  >
                    Remove file
                  </button>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSubmitReschedule}
                  disabled={rescheduleLoading || uploadingRescheduleDoc || !rescheduleReason.trim() || !rescheduleDate || !rescheduleSlot}
                  className="flex-1 bg-[#8B1818] text-white hover:bg-[#701313]"
                  data-testid="submit-reschedule-btn"
                >
                  {uploadingRescheduleDoc ? 'Uploading document...' : rescheduleLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
                <Button
                  onClick={() => { setShowRescheduleModal(false); setRescheduleBooking(null); }}
                  variant="outline"
                  className="flex-1"
                  data-testid="cancel-reschedule-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Viber Meeting Acknowledgment Modal */}
        {showViberModal && selectedViberBooking && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center" data-testid="viber-meeting-modal">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Video className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Join Viber Meeting</h3>
              <p className="text-slate-600 mb-2">
                You are about to join the Viber meeting for your consultation:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm"><strong>Date:</strong> {selectedViberBooking.date}</p>
                <p className="text-sm"><strong>Time:</strong> {selectedViberBooking.time_slot}</p>
                <p className="text-sm"><strong>Duration:</strong> {selectedViberBooking.duration} hour(s)</p>
              </div>
              <p className="text-xs text-slate-500 mb-6">
                This will open Viber in a new window. Make sure you have Viber installed on your device.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    window.open(selectedViberBooking.viber_link, '_blank');
                    setShowViberModal(false);
                    setSelectedViberBooking(null);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                  data-testid="confirm-join-viber-btn"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Viber Meeting
                </button>
                <button
                  onClick={() => { setShowViberModal(false); setSelectedViberBooking(null); }}
                  className="px-4 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors font-medium"
                  data-testid="cancel-viber-modal-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;
